"use client";

import Link from "next/link";
import { useRef, useState, type ReactNode } from "react";
import { Bath, BedDouble, MapPin, Maximize } from "lucide-react";

import { CarouselNormal } from "@/components/ui/carousel-swiper";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useEtiquetasDeFicha } from "./etiquetas-de-fichas";
import { CAPA } from "./capas";
import { ListingPrice } from "./listing-price";

/** Lo que el GeoJSON del mapa no trae y sí llega en la consulta de los resultados filtrados. */
export type DetallesDeFicha = {
  bathrooms?: number;
  etiquetas?: string[];
  zona?: string;
  publicadaEl?: number;
};

/**
 * La zona más concreta de un resultado de Algolia: «Valencia > Valencia Ciudad >
 * Extramurs > La Roqueta» da «La Roqueta». Los niveles vacíos llegan como "".
 */
export function zonaDeResultado(hit: Record<string, unknown>): string | undefined {
  const ruta = hit["location.lvl3"] || hit["location.lvl2"] || hit["location.lvl1"] || hit["location.lvl0"];
  return typeof ruta === "string" && ruta ? ruta.split(" > ").pop() : undefined;
}

/** Cuánto dura la etiqueta «Nuevo» desde que se dio de alta la ficha. */
const DIAS_DE_NOVEDAD = 14;

function esNueva(publicadaEl?: number) {
  return Boolean(publicadaEl) && Date.now() / 1000 - publicadaEl! < DIAS_DE_NOVEDAD * 24 * 60 * 60;
}

/** Cada familia de características con su color, para reconocerlas de un vistazo. */
function colorDeEtiqueta(etiqueta: string) {
  const t = etiqueta.toLowerCase();
  if (t.includes("piscina")) return "bg-sky-50 text-sky-700";
  if (t.includes("terraza") || t.includes("balc")) return "bg-amber-50 text-amber-800";
  if (t.includes("jard") || t.includes("zona verde")) return "bg-emerald-50 text-emerald-700";
  if (t.includes("vistas")) return "bg-cyan-50 text-cyan-700";
  if (t.includes("garaje") || t.includes("parking")) return "bg-indigo-50 text-indigo-700";
  if (t.includes("ascensor")) return "bg-violet-50 text-violet-700";
  return "bg-gray-100 text-gray-700";
}

function Etiqueta({ texto }: { texto: string }) {
  return (
    <span className={cn("rounded-md px-1.5 py-0.5 text-[11px] first-letter:uppercase", colorDeEtiqueta(texto))}>
      {texto}
    </span>
  );
}

/**
 * El «+N» con la nube de todas las características. Como el del CRM, se abre al pasar
 * por encima; y también al pulsarlo, porque en móvil no hay «pasar por encima». Es un
 * Popover y no un Tooltip porque el Tooltip no se abre con un toque.
 */
function MasEtiquetas({ restantes, todas, locale }: { restantes: number; todas: string[]; locale: string }) {
  const [abierta, setAbierta] = useState(false);
  const cierre = useRef<ReturnType<typeof setTimeout> | null>(null);

  const entrar = () => {
    if (cierre.current) clearTimeout(cierre.current);
    setAbierta(true);
  };
  // Con un margen, para que dé tiempo a llegar desde el «+N» hasta la nube.
  const salir = () => {
    cierre.current = setTimeout(() => setAbierta(false), 120);
  };

  return (
    <Popover open={abierta} onOpenChange={setAbierta}>
      <PopoverTrigger
        onMouseEnter={entrar}
        onMouseLeave={salir}
        aria-label={locale === "es" ? `Ver las ${todas.length} características` : `See all ${todas.length} features`}
        className="cursor-pointer rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-200"
      >
        +{restantes}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        collisionPadding={8}
        onMouseEnter={entrar}
        onMouseLeave={salir}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(CAPA, "w-72 p-3")}
      >
        <p className="mb-2 text-xs text-gray-500">
          {locale === "es" ? `${todas.length} características` : `${todas.length} features`}
        </p>
        <div className="flex flex-wrap gap-1">
          {todas.map((etiqueta) => (
            <Etiqueta key={etiqueta} texto={etiqueta} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const OPERACION = {
  venta: { es: "Venta", en: "Sale", clase: "bg-[#193144] text-white" },
  alquiler: { es: "Alquiler", en: "Rent", clase: "bg-emerald-600 text-white" },
  traspaso: { es: "Traspaso", en: "Transfer", clase: "bg-amber-500 text-amber-950" },
};

export function TarjetaPropiedad({
  titulo,
  images,
  slug,
  referencia,
  agreement_type,
  price_sale,
  price_rent,
  transfer,
  transfer_sale,
  transfer_rent,
  rooms,
  sqmts,
  bathrooms,
  etiquetas = [],
  caracteristicas,
  zona,
  provincia,
  publicadaEl,
  cta,
  locale,
  maximoDeEtiquetas = 3,
}: DetallesDeFicha & {
  titulo: ReactNode;
  /** `tags_es` de la ficha, si ya se ha leído: así la tarjeta no tiene que ir a buscarlo. */
  caracteristicas?: unknown;
  images: string[];
  slug: string;
  referencia: string;
  /** Valor de `operation_type_es` / `operation_type_en`. En un traspaso no cuenta: ver `ListingPrice`. */
  agreement_type?: string;
  price_sale?: number;
  price_rent?: number;
  transfer?: boolean;
  transfer_sale?: number;
  transfer_rent?: number;
  rooms?: number;
  sqmts?: number;
  provincia?: string;
  cta: string;
  locale: string;
  maximoDeEtiquetas?: number;
}) {
  const clave = transfer ? "traspaso" : agreement_type === "Venta" || agreement_type === "Sale" ? "venta" : "alquiler";
  const operacion = OPERACION[clave];
  const textoOperacion = locale === "es" ? operacion.es : operacion.en;
  const ruta = slug.startsWith("/") ? slug : `/${slug}`;

  const lugar = [zona, provincia]
    .filter((parte, i, partes): parte is string => Boolean(parte) && partes.indexOf(parte) === i)
    .join(", ");
  const todas = useEtiquetasDeFicha(referencia, etiquetas, locale, caracteristicas);
  const visibles = todas.slice(0, maximoDeEtiquetas);
  const restantes = todas.length - visibles.length;

  return (
    // `isolate`: los z-index de dentro (etiquetas, flechas de la foto) no compiten con la
    // cabecera fija del panel, que se les quedaba por debajo al hacer scroll.
    <article className="isolate flex h-full flex-col overflow-hidden border border-gray-200 bg-white shadow-xs transition-shadow hover:shadow-md">
      <div className="relative">
        <CarouselNormal slug={slug} images={images} slidesPerView={1} locale={locale} className="rounded-none" />
        <div className="pointer-events-none absolute left-3 top-3 z-20 flex gap-1.5">
          <span className={cn("rounded-md px-2 py-0.5 text-[11px] font-medium shadow-sm", operacion.clase)}>
            {textoOperacion}
          </span>
          {esNueva(publicadaEl) && (
            <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700 shadow-sm">
              {locale === "es" ? "Nuevo" : "New"}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2.5 p-4">
        <div className="flex items-start justify-between gap-3">
          <ListingPrice
            locale={locale}
            agreement_type={agreement_type}
            price_sale={price_sale}
            price_rent={price_rent}
            transfer={transfer}
            transfer_sale={transfer_sale}
            transfer_rent={transfer_rent}
          />
          <span className="mt-0.5 shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] tabular-nums text-gray-600">
            {referencia}
          </span>
        </div>

        <div className="min-w-0">
          <h3 className="truncate text-sm font-medium text-gray-900">
            <Link href={ruta} target="_blank" className="hover:underline">
              {titulo}
            </Link>
          </h3>
          {lugar && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
              <MapPin className="size-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{lugar}</span>
            </p>
          )}
        </div>

        {/* Un traspaso es casi siempre un local: las habitaciones valen 0 y no dicen nada. */}
        <div className="flex items-center gap-3 text-xs text-gray-700">
          {!transfer && Boolean(rooms) && (
            <span className="flex items-center gap-1">
              <BedDouble className="size-3.5 text-gray-400" aria-hidden="true" />
              {rooms} {locale === "es" ? "hab." : rooms === 1 ? "room" : "rooms"}
            </span>
          )}
          {!transfer && Boolean(bathrooms) && (
            <span className="flex items-center gap-1">
              <Bath className="size-3.5 text-gray-400" aria-hidden="true" />
              {bathrooms} {locale === "es" ? (bathrooms === 1 ? "baño" : "baños") : bathrooms === 1 ? "bath" : "baths"}
            </span>
          )}
          {Boolean(sqmts) && (
            <span className="flex items-center gap-1">
              <Maximize className="size-3.5 text-gray-400" aria-hidden="true" />
              {sqmts} m²
            </span>
          )}
        </div>

        {visibles.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {visibles.map((etiqueta) => (
              <Etiqueta key={etiqueta} texto={etiqueta} />
            ))}
            {restantes > 0 && <MasEtiquetas restantes={restantes} todas={todas} locale={locale} />}
          </div>
        )}

        {/* Abajo del todo: en una rejilla, las tarjetas de una fila miden lo mismo aunque
            a alguna le quepan menos etiquetas o no tenga. */}
        <div className="mt-auto pt-1">
          <Link
            href={ruta}
            target="_blank"
            className="block rounded-md bg-[#193144] py-2 text-center text-xs font-medium text-white! transition-opacity hover:opacity-85"
          >
            {cta}
          </Link>
        </div>
      </div>
    </article>
  );
}
